/**
 * Lookbook (sections/lookbook.liquid). Ported from the Next.js Lookbook.tsx: scroll moves the frames
 * sideways; the next frame waits small at the right edge and grows as it slides in. Settles on the
 * nearer frame when scrolling stops. Reduced motion: plain strip, no script.
 * Phones: a full-height frame under the season name, with the next one peeking at full height at the
 * right edge (a shrunken peek there read as a stray thumbnail and overlapped the current frame).
 */
const SMALL = 0.34;
const GAP = 16;
const PHONE_PEEK = 28;
const PHONE_GAP = 10;
/** Room under the frame for its caption (the dock, if any, is measured separately). */
const PHONE_CAPTION = 52;
const clamp01 = (n) => Math.min(1, Math.max(0, n));
const ease = (t) => t * t * (3 - 2 * t);

class YusheLookbook extends HTMLElement {
  connectedCallback() {
    const root = this;
    const stage = this.querySelector('.lkb__stage');
    if (!stage) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const els = [...this.querySelectorAll('.lkb__item')];
    const metas = [...this.querySelectorAll('.lkb__meta li')];
    const chapterEls = [...this.querySelectorAll('.lkb__chapter')];
    const chapters = chapterEls.map((el) => el.dataset.chapter);
    const frameChapters = els.map((el) => el.dataset.chapter);
    const n = els.length;
    if (!n) return;
    const ac = new AbortController();
    const { signal } = ac;
    this.ac = ac;

    let vw = 0;
    let vh = 0;
    let pin = 0;
    let top = 0;
    let fullH = 0;
    let fullW = 0;
    let anchor = 0;
    let right = 0;
    let small = SMALL;
    let gap = GAP;
    const chaptersEl = this.querySelector('.lkb__chapters');
    const measure = () => {
      vw = stage.clientWidth;
      vh = stage.clientHeight;
      pin = Math.max(1, root.offsetHeight - vh);
      const cs = getComputedStyle(stage);
      const padTop = parseFloat(cs.getPropertyValue('--pad-top')) || 96;
      const padBottom = parseFloat(cs.getPropertyValue('--pad-bottom')) || 96;
      const gutter = parseFloat(cs.getPropertyValue('--gutter-px')) || 16;
      const phone = vw < 768;
      if (phone) {
        // Frame starts under the season name and takes the height left above the caption.
        top = chaptersEl ? chaptersEl.offsetTop + chaptersEl.offsetHeight + 16 : padTop;
        fullW = vw - gutter * 2 - PHONE_PEEK - PHONE_GAP;
        // Down to the caption, which sits just above the floating dock.
        // (Measured from its size and resting offset, not its on-screen box, which is still sliding in on load.)
        const dock = document.querySelector('.dock');
        const dockRoom = dock && dock.getClientRects().length ? dock.offsetHeight + parseFloat(getComputedStyle(dock).bottom) : 0;
        const floor = vh - dockRoom;
        fullH = Math.min(fullW * 1.75, floor - top - PHONE_CAPTION);
        if (fullH < fullW * 1.2) fullW = fullH / 1.2;
        anchor = gutter;
        right = anchor + fullW + PHONE_GAP;
        small = 1;
        gap = PHONE_GAP;
      } else {
        fullH = vh - padTop - padBottom;
        fullW = fullH * 0.8;
        const maxW = vw * 0.42;
        if (fullW > maxW) {
          fullW = maxW;
          fullH = fullW / 0.8;
        }
        top = padTop + (vh - padTop - padBottom - fullH) / 2;
        anchor = Math.max(gutter, vw * 0.22);
        right = vw - gutter - fullW * SMALL;
        small = SMALL;
        gap = GAP;
      }
      for (const el of els) {
        el.style.width = `${fullW}px`;
        el.style.height = `${fullH}px`;
      }
      stage.style.setProperty('--full-w', `${fullW}px`);
      stage.style.setProperty('--anchor', `${anchor}px`);
      stage.style.setProperty('--frame-bottom', `${top + fullH}px`);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(stage);
    ro.observe(root);

    const target = () => clamp01(-root.getBoundingClientRect().top / pin) * (n - 1);
    let cur = target();
    let active = -1;

    const layout = () => {
      for (let i = 0; i < n; i++) {
        const el = els[i];
        const t = i - cur;
        let x;
        let s;
        if (t <= 0) {
          x = anchor + t * (anchor + fullW + Math.max(gap, 40));
          s = 1;
        } else if (t <= 1) {
          const e = ease(t);
          x = anchor + (right - anchor) * e;
          s = 1 + (small - 1) * e;
        } else {
          x = right + (t - 1) * (fullW * small + gap);
          s = small;
        }
        el.style.transform = `translate3d(${x.toFixed(1)}px,${(top + fullH * (1 - s)).toFixed(1)}px,0) scale(${s.toFixed(4)})`;
        const off = x > vw || x + fullW * s < 0;
        if (off !== el.hasAttribute('data-off')) el.toggleAttribute('data-off', off);
      }
      const a = Math.round(cur);
      if (a !== active) {
        active = a;
        const ch = chapters.indexOf(frameChapters[a]);
        els.forEach((el, i) => el.toggleAttribute('data-active', i === a));
        metas.forEach((el, i) => el.toggleAttribute('data-active', i === a));
        chapterEls.forEach((el, i) => {
          el.toggleAttribute('data-active', i === ch);
          el.toggleAttribute('data-before', i < ch);
        });
      }
      root.style.setProperty('--progress', (cur / Math.max(1, n - 1)).toFixed(4));
    };

    const scrollToFrame = (i) => {
      const y = window.scrollY + root.getBoundingClientRect().top + (i / Math.max(1, n - 1)) * pin;
      window.scrollTo({ top: y, behavior: 'smooth' });
    };

    let idle = 0;
    let touching = false;
    const settle = () => {
      if (touching) return;
      const rect = root.getBoundingClientRect();
      if (rect.top > 0 || rect.bottom < vh) return;
      const t = target();
      const near = Math.round(t);
      if (Math.abs(t - near) > 0.03) scrollToFrame(near);
    };
    const onScroll = () => {
      clearTimeout(idle);
      idle = window.setTimeout(settle, 160);
    };
    window.addEventListener('scroll', onScroll, { passive: true, signal });
    window.addEventListener('touchstart', () => {
      touching = true;
    }, { passive: true, signal });
    window.addEventListener('touchend', () => {
      touching = false;
      onScroll();
    }, { passive: true, signal });
    stage.addEventListener('click', (e) => {
      const a = e.target.closest('.lkb__item');
      if (!a) return;
      const i = els.indexOf(a);
      if (i !== active) {
        e.preventDefault();
        scrollToFrame(i);
      }
    }, { signal });
    stage.addEventListener('focusin', (e) => {
      const a = e.target.closest('.lkb__item');
      if (a && e.target instanceof HTMLElement && e.target.matches(':focus-visible')) scrollToFrame(els.indexOf(a));
    }, { signal });

    let raf = 0;
    const frame = () => {
      const t = target();
      cur += (t - cur) * 0.12;
      if (Math.abs(t - cur) < 0.001) cur = t;
      layout();
      raf = requestAnimationFrame(frame);
    };
    const io = new IntersectionObserver(([entry]) => {
      cancelAnimationFrame(raf);
      if (entry.isIntersecting) raf = requestAnimationFrame(frame);
    });
    io.observe(root);
    layout();
    root.setAttribute('data-ready', '');
    this.cleanup = () => {
      cancelAnimationFrame(raf);
      clearTimeout(idle);
      io.disconnect();
      ro.disconnect();
    };
  }

  disconnectedCallback() {
    if (this.ac) this.ac.abort();
    if (this.cleanup) this.cleanup();
  }
}

if (!customElements.get('yushe-lookbook')) customElements.define('yushe-lookbook', YusheLookbook);
